import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from '@/stores/useToastStore'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { Lock } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ProductsTableToolbar } from './ProductsTableToolbar'
import { BulkActionsBar } from './BulkActionsBar'
import { ProductsTablePagination } from './ProductsTablePagination'
import { ProductsTableDialogs } from './ProductsTableDialogs'
import {
  NameCell,
  OptionRowCell,
  SkuCell,
  LineActionsCell,
} from './cellRenderers'
import { DagCellRenderer } from './DagCellRenderer'
import { buildDagColumnDefs } from './buildDagColumnDefs'
import type { ColumnTotal } from './DagColumnHeaderWithTotals'
import { useDagTabNavigation } from './useDagTabNavigation'
import { useDagDropdownPaste } from './useDagDropdownPaste'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { AssignStoreModelDrawer } from './AssignStoreModelDrawer'
import { PlanWavesDrawer } from './PlanWavesDrawer'
import { MultilocDrawer } from './MultilocDrawer'
import { SecondaryAttributesDrawer } from './SecondaryAttributesDrawer'
import { ColumnsConfigurationDrawer } from './ColumnsConfigurationDrawer'
import { AssignParentSkuDrawer } from './AssignParentSkuDrawer'
import { ManagePIABDrawer } from './ManagePIABDrawer'
import { RetailPricingDrawer } from './RetailPricingDrawer'
import { ForecastModelDrawer } from './ForecastModelDrawer'
import { ManageLineImagesModal } from './ManageLineImagesModal'
import { ImageCarouselModal } from './ImageCarouselModal'
import { LineItemCommentsDrawer } from './LineItemCommentsDrawer'
import { ThumbnailTracker } from './ThumbnailTracker'
import {
  useProductsTableState,
  dagRowToRowData,
  rowDataToPlanDetailItem,
  type RowData,
} from './useProductsTableState'
import { useProductsTableMutations } from './useProductsTableMutations'
import {
  useDagMetadata,
  useDagRows,
  useDropdownLookups,
} from '@/hooks/useDagTable'
import { useUpdatePlanHeader } from '@/hooks/useAssortmentPlans'
import { useHierarchyForDag } from '@/hooks/useHierarchyForDag'
import { useDagSSE } from '@/hooks/useDagSSE'
import { useStuckComputingWatcher } from '@/hooks/useStuckComputingWatcher'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { MOCK_OPTIONS } from '@/mocks/productOptions'
import type { PlanDetailItem, ApiPlanStatus } from '@/types'
import type { DagRowRead } from '@/types/dag'
import { getConfigurableColumns } from './columnConfiguration'

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

interface ProductsTableProps {
  planId: string
  versionId: string | undefined
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onAddItem?: () => void
  onImport?: () => void
  onExport?: () => void
  isExporting?: boolean
  onBulkDeleteSuccess?: () => void
  isReadOnly?: boolean
  planStatus?: ApiPlanStatus
  primaryAttributes?: string[]
}

export function ProductsTable({
  planId,
  versionId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onAddItem,
  onImport,
  onExport,
  isExporting,
  onBulkDeleteSuccess,
  isReadOnly,
  planStatus,
  primaryAttributes = [],
}: ProductsTableProps) {
  // ---- Search state (debounced, sent to backend) --------------------------
  const [searchQuery, setSearchQuery] = useState('')
  const prevSearchRef = useRef(searchQuery)

  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery
      onPageChange(1)
    }
  }, [searchQuery, onPageChange])

  const handleSearch = useCallback((debouncedValue: string) => {
    setSearchQuery(debouncedValue)
  }, [])

  // ---- Data fetching -------------------------------------------------------
  const { data: metadata } = useDagMetadata(planId, versionId)
  const { data: rowsData, isLoading } = useDagRows(
    planId,
    versionId,
    page,
    pageSize,
    searchQuery
  )
  const { data: dropdownData } = useDropdownLookups()
  const hierarchyData = useHierarchyForDag()
  const updatePlanHeader = useUpdatePlanHeader(planId)

  // SSE live updates
  useDagSSE(planId, versionId)
  useStuckComputingWatcher(planId, versionId)

  // Column visibility
  const effectiveHiddenColumnIds = useColumnVisibility(
    planId,
    versionId,
    primaryAttributes,
    metadata?.columns
  )

  // Selectable columns (dynamic, non-Identity, non-fixed) for column selector
  // const FIXED_IDS = useMemo(() => new Set(['description', 'plan_sku_num']), [])
  const selectableColumns = useMemo(
    () => (metadata?.columns ? getConfigurableColumns(metadata.columns) : []),
    [metadata]
  )
  const selectableGroups = useMemo(
    () => metadata?.groups.filter(g => g !== 'Identity') ?? [],
    [metadata]
  )

  const rows: DagRowRead[] = useMemo(() => rowsData?.rows ?? [], [rowsData])

  // Store values (from patch-success full snapshot + SSE partial merges) win
  // over the rows-load snapshot, so in-flight updates are not clobbered when
  // the rows query refetches with stale cached data.
  const storeAggregates = useDagTableStore(s => s.aggregates)
  const columnTotals = useMemo<Record<string, ColumnTotal[]>>(() => {
    if (!metadata) return {}
    const merged = { ...(rowsData?.aggregates ?? {}), ...storeAggregates }
    const out: Record<string, ColumnTotal[]> = {}
    for (const col of metadata.columns) {
      if (col.kind !== 'aggregate') continue
      const anchor = col.deps?.[0]
      if (!anchor) continue
      const value = merged[col.id]
      if (value == null) continue
      ;(out[anchor] ??= []).push({
        id: col.id,
        label: col.label,
        format: col.format,
        value,
      })
    }
    return out
  }, [metadata, rowsData?.aggregates, storeAggregates])

  const totalCount = rowsData?.pagination.total ?? 0
  const totalPages = Math.ceil(totalCount / pageSize) || 1

  // Dropdown map: category -> options[]
  const dropdownMap = useMemo(
    () => dropdownData?.categories ?? {},
    [dropdownData]
  )

  // Column metadata map: column_id -> DagColumnMeta
  const columnsMap = useMemo(() => {
    if (!metadata) return new Map()
    return new Map(metadata.columns.map(c => [c.id, c]))
  }, [metadata])

  // Row version lookup for PATCH OCC
  const rowVersionMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const r of rows) map.set(r.id, r.row_version)
    return map
  }, [rows])

  const getRowVersion = useCallback(
    (rowId: string) => rowVersionMap.get(rowId) ?? 0,
    [rowVersionMap]
  )

  // ---- State management ----------------------------------------------------
  const state = useProductsTableState(rows)

  const getBulkSelectedRows = useCallback(
    (rowId: string) => {
      const selectedIds = state.selectedItemIdsRef.current
      if (selectedIds.size <= 1 || !selectedIds.has(rowId)) return null

      const selectedRows = Array.from(
        state.selectedItemsCacheRef.current.values()
      ).filter(row => selectedIds.has(row.id))

      return selectedRows.length > 1 ? selectedRows : null
    },
    [state.selectedItemIdsRef, state.selectedItemsCacheRef]
  )

  // Tab navigation for DAG cells
  const navigateTab = useDagTabNavigation(state.gridRef, columnsMap)
  const clearFocus = useDagTableStore(s => s.clearFocus)
  const highlightedRowId = useDagTableStore(s => s.highlightedRowId)

  // Re-apply row classes when audit highlight changes
  useEffect(() => {
    state.gridRef.current?.api?.redrawRows()
  }, [highlightedRowId, state.gridRef])

  // Click-away: clear focus when clicking outside the grid
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const gridEl = document.querySelector(
        '.ag-theme-michaels.plan-detail-grid'
      ) as HTMLElement | null
      if (gridEl && !gridEl.contains(e.target as Node)) {
        // Don't clear focus if clicking inside a portal dropdown
        const target = e.target as HTMLElement
        if (target.closest('.dag-dropdown-portal')) return
        clearFocus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [clearFocus])

  // Mutations with callbacks
  const mutations = useProductsTableMutations(
    planId,
    {
      onEditSuccess: state.clearEditingCell,
      onRowsPatched: state.applyPatchedRowsToSelectionCache,
      onRowRefreshNeeded: (rowId: string) => {
        if (state.gridRef.current?.api) {
          const rowNode = state.gridRef.current.api.getRowNode(rowId)
          if (rowNode) {
            state.gridRef.current.api.refreshCells({
              rowNodes: [rowNode],
              force: true,
            })
          }
        }
      },
      onCopySuccess: state.closeCopyDialog,
      onDeleteSuccess: () => {
        if (state.dialogState.selectedItemId) {
          state.handleSelectItem(state.dialogState.selectedItemId, false)
        }
        state.closeDeleteDialog()
      },
    },
    {
      versionId,
      columnsMap,
      userId: 'current-user',
    }
  )

  useDagDropdownPaste({
    isReadOnly,
    columnsMap,
    rows,
    dropdownMap,
    hierarchyData,
    getRowVersion,
    handleCellEdit: mutations.handleCellEdit,
    handleMultiCellEdit: mutations.handleMultiCellEdit,
  })

  // ---- Drawer / modal states -----------------------------------------------
  const [assignStoreModelState, setAssignStoreModelState] = useState<{
    open: boolean
    lineIds: string[]
    existingStoreModelId: string | null
    startAtGroups: boolean
    initialStoreGroupIds?: string[]
  }>({
    open: false,
    lineIds: [],
    existingStoreModelId: null,
    startAtGroups: false,
  })

  const [planWavesState, setPlanWavesState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [multilocState, setMultilocState] = useState<{
    modalOpen: boolean
    drawerOpen: boolean
    item: PlanDetailItem | null
  }>({ modalOpen: false, drawerOpen: false, item: null })

  const [columnsConfigOpen, setColumnsConfigOpen] = useState(false)

  const [secondaryDrawerState, setSecondaryDrawerState] = useState<{
    open: boolean
    rowId: string | null
  }>({ open: false, rowId: null })
  const [promotingPrimaryColumnId, setPromotingPrimaryColumnId] = useState<
    string | null
  >(null)

  const [assignParentState, setAssignParentState] = useState<{
    open: boolean
    rowId: string | null
  }>({ open: false, rowId: null })

  const [piabState, setPiabState] = useState<{
    open: boolean
    rowId: string | null
  }>({ open: false, rowId: null })

  const [manageImagesState, setManageImagesState] = useState<{
    open: boolean
    lineId: string | null
  }>({ open: false, lineId: null })

  const [carouselState, setCarouselState] = useState<{
    open: boolean
    lineId: string | null
  }>({ open: false, lineId: null })

  const [retailPricingState, setRetailPricingState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [forecastModelState, setForecastModelState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [storeModelRequiredState, setStoreModelRequiredState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [openCommentsRefId, setOpenCommentsRefId] = useState<string | null>(
    null
  )
  const handleOpenComments = useCallback((lineRefId: string) => {
    setOpenCommentsRefId(lineRefId)
  }, [])

  // Deep-link: ?openComments=<line_ref_id> opens the drawer once rows load
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandledRef = useRef<string | null>(null)
  useEffect(() => {
    const deepRefId = searchParams.get('openComments')
    if (!deepRefId || deepLinkHandledRef.current === deepRefId) return
    if (rows.length === 0) return
    const match = rows.find(r => r.line_ref_id === deepRefId)
    if (!match) return
    deepLinkHandledRef.current = deepRefId
    setOpenCommentsRefId(deepRefId)
    const next = new URLSearchParams(searchParams)
    next.delete('openComments')
    setSearchParams(next, { replace: true })
  }, [rows, searchParams, setSearchParams])

  const commentsDrawerRow = useMemo(() => {
    if (!openCommentsRefId) return null
    return rows.find(r => r.line_ref_id === openCommentsRefId) ?? null
  }, [rows, openCommentsRefId])

  const handleAssignStoreGroup = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const existingId = (row?.cells['store_model_id'] as string) ?? null
      const activeGroupIds =
        (row?.cells['active_store_group_ids'] as string[]) ?? undefined
      setAssignStoreModelState({
        open: true,
        lineIds: [itemId],
        existingStoreModelId: existingId,
        startAtGroups: false,
        initialStoreGroupIds: activeGroupIds,
      })
    },
    [rows]
  )

  const handlePlanWaves = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      setPlanWavesState({ open: true, item })
    },
    [rows]
  )

  const handleMultiloc = useCallback((item: PlanDetailItem) => {
    setMultilocState({ modalOpen: false, drawerOpen: true, item })
  }, [])

  const handleBulkAssignStoreGroup = useCallback(() => {
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    const allSame = storeModelIds.every(id => id && id === storeModelIds[0])
    setAssignStoreModelState({
      open: true,
      lineIds: ids,
      existingStoreModelId: allSame ? storeModelIds[0] : null,
      startAtGroups: false,
    })
  }, [state.selectedItemIds, rows])

  const handleBulkManageStoreGroups = useCallback(() => {
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    const sharedModelId = storeModelIds[0]
    setAssignStoreModelState({
      open: true,
      lineIds: ids,
      existingStoreModelId: sharedModelId,
      startAtGroups: true,
    })
  }, [state.selectedItemIds, rows])

  const canManageStoreGroups = useMemo(() => {
    if (state.selectedItemIds.size === 0) return false
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    return storeModelIds.every(id => id != null && id === storeModelIds[0])
  }, [state.selectedItemIds, rows])

  const handleViewSecondaryAttributes = useCallback((itemId: string) => {
    setSecondaryDrawerState({ open: true, rowId: itemId })
  }, [])

  const handleAssignParentSku = useCallback((itemId: string) => {
    setAssignParentState({ open: true, rowId: itemId })
  }, [])

  const handleManagePIAB = useCallback((itemId: string) => {
    setPiabState({ open: true, rowId: itemId })
  }, [])

  const handleManageImages = useCallback((itemId: string) => {
    setManageImagesState({ open: true, lineId: itemId })
  }, [])

  const handleOpenCarousel = useCallback((lineId: string) => {
    setCarouselState({ open: true, lineId })
  }, [])

  const handleCreatePricing = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      setRetailPricingState({ open: true, item })
    },
    [rows]
  )

  const handleCreateForecast = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      if (!item?.storeModelId) {
        setStoreModelRequiredState({ open: true, item })
      } else {
        setForecastModelState({ open: true, item })
      }
    },
    [rows]
  )

  const handleGoToStoreModel = useCallback(() => {
    const itemId = storeModelRequiredState.item?.id
    if (!itemId) return
    setStoreModelRequiredState({ open: false, item: null })
    setAssignStoreModelState({
      open: true,
      lineIds: [itemId],
      existingStoreModelId: null,
      startAtGroups: false,
    })
  }, [storeModelRequiredState.item])

  const secondaryDrawerRow = useMemo(() => {
    if (!secondaryDrawerState.rowId) return null
    return rows.find(row => row.id === secondaryDrawerState.rowId) ?? null
  }, [rows, secondaryDrawerState.rowId])

  const handlePromoteToPrimary = useCallback(
    (columnId: string) => {
      if (primaryAttributes.includes(columnId)) return

      const nextPrimaryAttributes = Array.from(
        new Set([...primaryAttributes, columnId])
      )

      setPromotingPrimaryColumnId(columnId)
      updatePlanHeader.mutate(
        { primary_attribute_ids: nextPrimaryAttributes },
        {
          onError: () => {
            toast.error('Failed to promote attribute to primary')
          },
          onSettled: () => {
            setPromotingPrimaryColumnId(null)
          },
        }
      )
    },
    [primaryAttributes, updatePlanHeader]
  )

  // ---- Build row data with option rows ------------------------------------
  const rowData: RowData[] = useMemo(() => {
    const result: RowData[] = []
    for (const row of rows) {
      result.push(dagRowToRowData(row))

      if (state.expandedItems.has(row.id)) {
        const options = MOCK_OPTIONS.default
        for (const option of options) {
          result.push({
            id: `${row.id}-${option.id}`,
            isOptionRow: true,
            option,
            parentId: row.id,
          })
        }
      }
    }
    return result
  }, [rows, state.expandedItems])

  // ---- Column definitions --------------------------------------------------

  // Header component with checkbox
  const NameHeaderComponent = useCallback(() => {
    return (
      <div className="flex items-center gap-2.5 pl-4">
        <div className="w-6" />
        <Checkbox
          checked={state.allSelectedRef.current}
          onCheckedChange={state.handleSelectAll}
          className="cursor-pointer"
          {...(state.someSelectedRef.current
            ? { 'data-state': 'indeterminate' }
            : {})}
        />
        <span>{columnsMap.get('description')?.label ?? 'Description'}</span>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.handleSelectAll, columnsMap])

  // Name cell renderer
  const NameCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      const row = props.data
      if (!row) return null

      if (row.isOptionRow && row.option) {
        return <OptionRowCell option={row.option} />
      }

      return (
        <NameCell
          {...(props as Parameters<typeof NameCell>[0])}
          data={row}
          isSelected={state.selectedItemIdsRef.current.has(row.id)}
          onToggleSelect={checked => state.handleSelectItem(row.id, checked)}
          editingCell={state.editingCell}
          onStartEdit={state.handleStartEdit}
          onCancelEdit={state.handleCancelEdit}
          onSaveEdit={(id, _field, value) => {
            const selectedRows = getBulkSelectedRows(id)
            if (selectedRows) {
              mutations.handleBulkCellEdit(selectedRows, 'description', value)
              return
            }

            mutations.handleDescriptionSave(id, value, getRowVersion(id))
          }}
          navigateTab={navigateTab}
          isReadOnly={isReadOnly}
          onOpenCarousel={handleOpenCarousel}
        />
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.handleSelectItem,
      state.editingCell,
      state.handleStartEdit,
      state.handleCancelEdit,
      mutations.handleDescriptionSave,
      mutations.handleBulkCellEdit,
      getBulkSelectedRows,
      getRowVersion,
      navigateTab,
      isReadOnly,
      handleOpenCarousel,
    ]
  )

  // SKU cell renderer
  const SkuCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      if (!props.data || props.data.isOptionRow) return null
      return (
        <SkuCell
          {...(props as Parameters<typeof SkuCell>[0])}
          editingCell={state.editingCell}
          onStartEdit={state.handleStartEdit}
          onCancelEdit={state.handleCancelEdit}
          onSkuSave={mutations.handleSkuSave}
          isReadOnly={isReadOnly}
        />
      )
    },
    [
      state.editingCell,
      state.handleStartEdit,
      state.handleCancelEdit,
      mutations.handleSkuSave,
      isReadOnly,
    ]
  )

  // Actions cell renderer
  const ActionsCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      const row = props.data
      if (!row || row.isOptionRow) return null
      return (
        <LineActionsCell
          {...(props as Parameters<typeof LineActionsCell>[0])}
          data={row}
          onAssignStoreGroup={handleAssignStoreGroup}
          onMultiloc={handleMultiloc}
          onViewSecondaryAttributes={handleViewSecondaryAttributes}
          onPlanWaves={handlePlanWaves}
          onCopyLine={state.openCopyDialog}
          onDeleteLine={state.openDeleteDialog}
          triggerAutoSave={state.handleAutoSaveBeforeMenu}
          isReadOnly={isReadOnly}
          onAssignParentSku={handleAssignParentSku}
          onManagePIAB={handleManagePIAB}
          onCreatePricing={handleCreatePricing}
          onCreateForecast={handleCreateForecast}
          onManageImages={handleManageImages}
          onOpenComments={handleOpenComments}
        />
      )
    },
    [
      state.openCopyDialog,
      state.openDeleteDialog,
      state.handleAutoSaveBeforeMenu,
      handleAssignStoreGroup,
      handleMultiloc,
      handleViewSecondaryAttributes,
      handlePlanWaves,
      isReadOnly,
      handleAssignParentSku,
      handleManagePIAB,
      handleCreatePricing,
      handleCreateForecast,
      handleManageImages,
      handleOpenComments,
    ]
  )

  // Build column defs from DAG metadata
  const columnDefs = useMemo(() => {
    if (!metadata) return []
    return buildDagColumnDefs({
      columns: metadata.columns,
      groups: metadata.groups,
      dropdownMap,
      hiddenColumnIds: effectiveHiddenColumnIds,
      isReadOnly,
      columnTotals,
      NameHeaderComponent,
      NameCellRenderer,
      SkuCellRenderer,
      ActionsCellRenderer,
      DagCellRendererComponent: DagCellRenderer,
    })
  }, [
    metadata,
    dropdownMap,
    effectiveHiddenColumnIds,
    isReadOnly,
    columnTotals,
    NameHeaderComponent,
    NameCellRenderer,
    SkuCellRenderer,
    ActionsCellRenderer,
  ])

  // Memoize AG Grid context to avoid unnecessary re-renders of all cells
  const gridContext = useMemo(
    () => ({
      onCellEdit: mutations.handleCellEdit,
      onMultiCellEdit: mutations.handleMultiCellEdit,
      onBulkCellEdit: mutations.handleBulkCellEdit,
      onBulkMultiCellEdit: mutations.handleBulkMultiCellEdit,
      getBulkSelectedRows,
      getRowVersion,
      navigateTab,
      isReadOnly: isReadOnly ?? false,
      dropdownMap,
      hierarchyData,
      planId,
      versionId,
    }),
    [
      mutations.handleCellEdit,
      mutations.handleMultiCellEdit,
      mutations.handleBulkCellEdit,
      mutations.handleBulkMultiCellEdit,
      getBulkSelectedRows,
      getRowVersion,
      navigateTab,
      isReadOnly,
      dropdownMap,
      hierarchyData,
      planId,
      versionId,
    ]
  )

  // ---- Render --------------------------------------------------------------
  if (isLoading && rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-center p-12 text-muted-foreground">
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div>
      {isReadOnly && (
        <div className="flex items-center gap-2 px-4 py-2 mb-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
          <Lock className="h-4 w-4 shrink-0" />
          <span>
            {planStatus === 'APPROVED'
              ? 'This version is locked because the plan has been approved.'
              : 'This version is locked because the plan is pending approval.'}
          </span>
        </div>
      )}

      <ProductsTableToolbar
        onSearch={handleSearch}
        onAddItem={onAddItem}
        onImport={onImport}
        onExport={onExport}
        isExporting={isExporting}
        columns={selectableColumns}
        groups={selectableGroups}
        isReadOnly={isReadOnly}
        onOpenColumnsConfig={() => setColumnsConfigOpen(true)}
      />

      {state.selectedItemIds.size > 0 && (
        <BulkActionsBar
          planId={planId}
          versionId={versionId}
          selectedCount={state.selectedItemIds.size}
          selectedItems={state.selectedItemsData}
          onClearSelection={state.handleClearSelection}
          onDeleteSuccess={onBulkDeleteSuccess}
          onAssignStoreGroup={handleBulkAssignStoreGroup}
          onManageStoreGroups={handleBulkManageStoreGroups}
          canManageStoreGroups={canManageStoreGroups}
          isReadOnly={isReadOnly}
        />
      )}

      <div className="w-full">
        <div className="border border-[#e7e7e7] bg-white rounded-md">
          <div className="ag-theme-michaels plan-detail-grid w-full">
            <AgGridReact<RowData>
              ref={state.gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              theme="legacy"
              loading={isLoading}
              domLayout="autoHeight"
              rowHeight={68}
              headerHeight={49}
              groupHeaderHeight={49}
              overlayLoadingTemplate="<span>Loading...</span>"
              overlayNoRowsTemplate="<span>No items found</span>"
              suppressCellFocus={true}
              suppressRowHoverHighlight={false}
              suppressColumnVirtualisation={true}
              suppressMovableColumns={true}
              context={gridContext}
              getRowId={params => params.data.id}
              getRowClass={params => {
                const classes: string[] = []
                if (params.data?.isOptionRow) {
                  classes.push('ag-row-option')
                }
                if (
                  params.data?.id &&
                  state.expandedItems.has(params.data.id)
                ) {
                  classes.push('ag-row-expanded')
                }
                if (highlightedRowId && params.data?.id === highlightedRowId) {
                  classes.push('ag-row-audit-highlight')
                }
                return classes.length ? classes.join(' ') : undefined
              }}
            />
          </div>

          <ProductsTablePagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[20, 50, 100, 200]}
          />
        </div>
      </div>

      <ProductsTableDialogs
        copyDialogOpen={state.dialogState.copyOpen}
        onCopyDialogChange={open => {
          if (!open) state.closeCopyDialog()
        }}
        onConfirmCopy={() => {
          if (state.dialogState.selectedItemId) {
            mutations.handleConfirmCopy(state.dialogState.selectedItemId)
          }
        }}
        copyIsLoading={mutations.copyItemMutation.isPending}
        deleteDialogOpen={state.dialogState.deleteOpen}
        onDeleteDialogChange={open => {
          if (!open) state.closeDeleteDialog()
        }}
        onConfirmDelete={() => {
          if (state.dialogState.selectedItemId) {
            mutations.handleConfirmDelete(state.dialogState.selectedItemId)
          }
        }}
        deleteIsLoading={mutations.deleteItemMutation.isPending}
      />

      {versionId && (
        <AssignStoreModelDrawer
          open={assignStoreModelState.open}
          onOpenChange={open =>
            setAssignStoreModelState(prev => ({ ...prev, open }))
          }
          planId={planId}
          versionId={versionId}
          lineIds={assignStoreModelState.lineIds}
          existingStoreModelId={assignStoreModelState.existingStoreModelId}
          startAtGroups={assignStoreModelState.startAtGroups}
          initialStoreGroupIds={assignStoreModelState.initialStoreGroupIds}
          onSuccess={state.handleClearSelection}
        />
      )}

      {versionId && (
        <PlanWavesDrawer
          open={planWavesState.open}
          onOpenChange={open => setPlanWavesState(prev => ({ ...prev, open }))}
          item={planWavesState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      {versionId && (
        <RetailPricingDrawer
          open={retailPricingState.open}
          onOpenChange={open =>
            setRetailPricingState(prev => ({ ...prev, open }))
          }
          item={retailPricingState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      {versionId && (
        <ForecastModelDrawer
          open={forecastModelState.open}
          onOpenChange={open =>
            setForecastModelState(prev => ({ ...prev, open }))
          }
          item={forecastModelState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      <Dialog
        open={storeModelRequiredState.open}
        onOpenChange={open =>
          setStoreModelRequiredState(prev => ({ ...prev, open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Store Model Required</DialogTitle>
            <DialogDescription>
              You must define a Store Model before creating a Forecast Model.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setStoreModelRequiredState({ open: false, item: null })
              }
            >
              Cancel
            </Button>
            <Button className="cursor-pointer" onClick={handleGoToStoreModel}>
              Go to Store Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {versionId && (
        <MultilocDrawer
          open={multilocState.drawerOpen}
          onOpenChange={open =>
            setMultilocState(prev => ({ ...prev, drawerOpen: open }))
          }
          item={multilocState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      <SecondaryAttributesDrawer
        open={secondaryDrawerState.open}
        onOpenChange={open =>
          setSecondaryDrawerState(prev => ({ ...prev, open }))
        }
        row={secondaryDrawerRow}
        columns={metadata?.columns ?? []}
        primaryAttributes={primaryAttributes}
        dropdownMap={dropdownMap}
        onCellEdit={mutations.handleCellEdit}
        onPromoteToPrimary={handlePromoteToPrimary}
        promotingColumnId={promotingPrimaryColumnId}
        isReadOnly={isReadOnly}
      />

      <ColumnsConfigurationDrawer
        open={columnsConfigOpen}
        onOpenChange={setColumnsConfigOpen}
        columns={metadata?.columns ?? []}
        primaryAttributes={primaryAttributes}
        planId={planId}
      />

      {versionId &&
        assignParentState.rowId &&
        (() => {
          const row = rows.find(r => r.id === assignParentState.rowId)
          return (
            <AssignParentSkuDrawer
              open={assignParentState.open}
              onOpenChange={open =>
                setAssignParentState(prev => ({ ...prev, open }))
              }
              planId={planId}
              versionId={versionId}
              childRowId={assignParentState.rowId}
              childSkuNumber={(row?.cells['plan_sku_num'] as string) ?? null}
              existingParent={row?.parent_line_item ?? null}
            />
          )
        })()}

      {versionId &&
        piabState.rowId &&
        (() => {
          const row = rows.find(r => r.id === piabState.rowId)
          return (
            <ManagePIABDrawer
              open={piabState.open}
              onOpenChange={open => setPiabState(prev => ({ ...prev, open }))}
              planId={planId}
              versionId={versionId}
              parentRowId={piabState.rowId}
              parentSkuNumber={(row?.cells['plan_sku_num'] as string) ?? null}
              parentDescription={(row?.cells['description'] as string) ?? null}
            />
          )
        })()}

      <ThumbnailTracker />

      {versionId && manageImagesState.lineId && (
        <ManageLineImagesModal
          open={manageImagesState.open}
          onOpenChange={open =>
            setManageImagesState(prev => ({ ...prev, open }))
          }
          planId={planId}
          versionId={versionId}
          lineId={manageImagesState.lineId}
        />
      )}

      <LineItemCommentsDrawer
        open={!!openCommentsRefId}
        onOpenChange={open => {
          if (!open) setOpenCommentsRefId(null)
        }}
        lineRefId={openCommentsRefId}
        row={commentsDrawerRow}
      />

      {versionId && carouselState.lineId && (
        <ImageCarouselModal
          open={carouselState.open}
          onOpenChange={open => setCarouselState(prev => ({ ...prev, open }))}
          planId={planId}
          versionId={versionId}
          lineId={carouselState.lineId}
        />
      )}
    </div>
  )
}
